import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { CheckCircle } from "lucide-react";
import { appClient } from "@/api/appClient";

export default function QuickSetupCaseTypes() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const defaultCaseTypes = [
    {
      name: "Family-Based Immigration",
      pricing_type: "flat",
      default_price: 5000,
      display_order: 1,
      config: {
        person_labels: {
          primary_person_label: "Beneficiary",
          show_dependents: true,
          dependent_label: "Dependent"
        }
      }
    },
    {
      name: "Employment-Based Immigration",
      pricing_type: "flat",
      default_price: 7500,
      display_order: 2,
      config: {
        person_labels: {
          primary_person_label: "Applicant",
          show_dependents: false
        }
      }
    },
    {
      name: "Asylum",
      pricing_type: "flat",
      default_price: 4000,
      display_order: 3,
      config: {
        person_labels: {
          primary_person_label: "Applicant",
          show_dependents: true,
          dependent_label: "Dependent"
        }
      }
    },
    {
      name: "Naturalization",
      pricing_type: "flat",
      default_price: 3000,
      display_order: 4,
      config: {
        person_labels: {
          primary_person_label: "Applicant",
          show_dependents: false
        }
      }
    }
  ];

  const handleSetup = async () => {
    setIsProcessing(true);
    setResult(null);

    const results = [];

    for (const caseType of defaultCaseTypes) {
      try {
        const response = await appClient.functions.invoke('createSalesItem', caseType);
        results.push({
          name: caseType.name,
          success: response.data.success,
          error: response.data.error
        });
      } catch (error) {
        results.push({
          name: caseType.name,
          success: false,
          error: error.message
        });
      }
    }

    setResult(results);
    setIsProcessing(false);
  };

  return (
    <div className="bg-slate-50 p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-slate-900 mb-6">Quick Setup: Case Types</h1>

        <Card>
          <CardHeader>
            <CardTitle>Add Default Case Types</CardTitle>
            <CardDescription>
              Click below to add 4 common case types to your sales_items table
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              {defaultCaseTypes.map((ct, i) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{ct.name}</p>
                    <p className="text-sm text-slate-600">${ct.default_price.toLocaleString()} - {ct.pricing_type}</p>
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={handleSetup}
              disabled={isProcessing}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {isProcessing ? "Setting up..." : "Add These Case Types"}
            </Button>

            {result && (
              <div className="mt-4 space-y-2">
                {result.map((r, i) => (
                  <div
                    key={i}
                    className={`p-3 rounded-lg ${
                      r.success
                        ? "bg-green-50 border border-green-200"
                        : "bg-rose-50 border border-rose-200"
                    }`}
                  >
                    <p className="font-semibold">{r.name}</p>
                    {r.success ? (
                      <p className="text-sm text-green-700">✅ Added successfully</p>
                    ) : (
                      <p className="text-sm text-rose-700">❌ {r.error}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}